import type { SupabaseClient } from "@supabase/supabase-js";
import type { UIMessage } from "ai";

import type { AgentChatSession } from "@fitness-app/data-models";
import type { Database } from "@fitness-app/supabase";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import {
  createSalesAgentSessionMetadata,
  createSessionTitle,
  salesAgentChatType,
  type SalesAgentModelOptionId,
} from "./constants";
import { getMessageText } from "./message-utils";
import type { TFunction } from "i18next";

type AgentChatSupabaseClient = SupabaseClient<Database>;

export const persistAgentChatMessage = async (
  supabase: AgentChatSupabaseClient,
  { sessionId, message, modelId }: { sessionId: string; message: UIMessage; modelId: SalesAgentModelOptionId },
): Promise<void> => {
  const now = new Date().toISOString();
  const { error } = await supabase.from("agent_chat_message").upsert({
    id: message.id,
    sessionId,
    role: message.role,
    content: getMessageText(message),
    parts: message.parts,
    createdAt: now,
  });

  if (error) throw new Error(error.message);

  const { error: sessionError } = await supabase
    .from("agent_chat_session")
    .update({
      lastMessageAt: now,
      updatedAt: now,
      metadata: createSalesAgentSessionMetadata(modelId),
    })
    .eq("id", sessionId);

  if (sessionError) throw new Error(sessionError.message);
};

export const ensureAgentChatSession = async (
  tUi: TFunction,
  supabase: AgentChatSupabaseClient,
  {
    activeSessionId,
    firstMessageText,
    modelId,
    optimistic,
    sessionId,
  }: {
    activeSessionId: string | null;
    firstMessageText: string;
    modelId: SalesAgentModelOptionId;
    optimistic?: boolean;
    sessionId?: string;
  },
): Promise<AgentChatSession> => {
  if (activeSessionId && !optimistic) {
    const { data, error } = await supabase
      .from("agent_chat_session")
      .select("*")
      .eq("id", activeSessionId)
      .eq("agentType", salesAgentChatType)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (data) return data as AgentChatSession;
  }

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) throw new Error(userError?.message || tUi<string>("uiAccount:no-active-session"));

  const now = new Date().toISOString();
  const newSessionId = sessionId ?? generateUniqId();
  const { data, error } = await supabase
    .from("agent_chat_session")
    .insert({
      id: newSessionId,
      agentType: salesAgentChatType,
      ownerId: user.id,
      title: createSessionTitle(tUi, firstMessageText),
      createdAt: now,
      updatedAt: now,
      lastMessageAt: now,
      metadata: createSalesAgentSessionMetadata(modelId),
    })
    .select("*")
    .single();

  if (error) throw new Error(error.message);

  return data as AgentChatSession;
};
